import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import {Category} from "../../../../api/Products";
import {Loader} from "../../../../misc/Loader";
import {List, ListItemButton, ListItemText, TextField, useTheme} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {css} from "@emotion/css";
import {grey} from "@mui/material/colors";
import {first} from "nate-react-api-helpers";
import {CategoryKind} from "./CreateProduct";
import {CategoryContext} from "./CategoryProvider";

type CategoryNode = Category & {
    children: CategoryNode[],
    parent?: CategoryNode
}

function walk(nd: {children: CategoryNode[]}, cb: (input: {children: CategoryNode[]}) => void) {
    cb(nd);

    nd.children.forEach(c => {
        walk(c, cb);
    });
}

export function CategoryTree(props: {
    value: number | null;
    mode: CategoryKind;
    onCategoryName?(value: string): void;
    onChange(value: number | null, info?: {level: number; isAutoQtyHinge: boolean}): void;
}) {
    const mode = props.mode;
    const catCtx = useContext(CategoryContext)
    const result = useMemo(() => catCtx.categories.filter(c => !c.hideInProductEditor), [catCtx.categories]);
    const [selected, setSelected] = useState<CategoryNode>();
    const selectedRef = useRef(selected);
    selectedRef.current = selected;

    const tree = useMemo(() => {

        const map = result
            .filter(r => mode === null || r.kind === mode)
            .reduce((acc, r) => {
                acc[r.id] = Object.assign({}, r, { children: [] });
                return acc;
            }, {} as {[id: number]: CategoryNode});

        const root = {
            children: [] as CategoryNode[],
        };

        for(let i in map) {
            const el = map[i];
            if(!el.parentCategory) {
                root.children.push(el);
                continue;
            }

            map[el.parentCategory].children.push(el);
            el.parent = map[el.parentCategory];
        }

        walk(root, nd => {
            nd.children.sort((a, b) => {
                if(a.sortOrder === null) {
                    if(b.sortOrder === null) return 0;
                    return 1;
                }

                if(b.sortOrder === null) return -1;

                return a.sortOrder - b.sortOrder;
            });
        })

        return root;
    }, [result, mode]);

    const onChange = props.onChange;
    const propValue = props.value;
    const loaded = result.length > 0 && tree.children.length > 0;

    useEffect(() => {
        if(!loaded) return;
        if(!propValue) return;

        let nodes = [tree];
        while(nodes.length > 0) {

            for (let i = 0; i < nodes.length; i++) {
                const node = nodes[i];

                const find = first(node.children, c => c.id === propValue);
                if (find) {
                    setSelected(find);
                    selectedRef.current = find;
                    return;
                }
            }

            let nextNodes: any = [];
            nodes.map(n => nextNodes.push(...n.children));
            nodes = nextNodes;
        }

        // clear parent result because it doesn't exist
        onChange(null);
    }, [propValue, tree, loaded, onChange])

    const onCategoryName = props.onCategoryName;
    useEffect(() => {
        if(!loaded) return;

        // use ref b/c of race with useEffect above
        const selected = selectedRef.current;
        if(!selected) {
            onChange(null);
            return;
        }

        let cursor: any = selected;
        let level = 0;
        let list: (typeof selected)[] = [];

        while(cursor) {
            list.push(cursor);
            cursor = cursor.parent;

            level++
        }

        onChange(selected.id, {
            level: level,
            isAutoQtyHinge: list.filter(l => l?.name.toLowerCase().indexOf("hinge") !== -1 && l?.name.toLowerCase().indexOf("continuous") === -1).length > 0,
        })

        if(onCategoryName) onCategoryName(selected.name);
    }, [selected, onChange, onCategoryName, propValue, loaded]);

    const [search, setSearch] = useState("");

    return <Loader {...catCtx}>
        {() => {
            return (
                <div style={{
                    flex: 1,
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                }}>
                    <List dense className={listClass} style={{flex: 1, overflow: "auto"}}>
                        <ListItemButton className={selectedCategoryClass} onClick={() => setSelected(undefined)}>
                            <ListItemText primary="Category" />
                            <ExpandMoreIcon />
                        </ListItemButton>

                        {search === "" ? <>
                                <Item selected={selected} tree={tree as CategoryNode} setSelected={setSelected} />
                            </> : <>
                                {tree.children.map(c => <SearchItem node={c} search={search} selected={selected} onSelect={setSelected} />)}
                            </>}
                    </List>
                    <div style={{height: 8}} />
                    <TextField fullWidth size="small" label="Search" value={search} onChange={e => setSearch(e.target.value)} />
                </div>
            )
        }}
    </Loader>
}

function SearchItem(props: {
    node: CategoryNode;
    search: string;
    selected?: CategoryNode;
    showAll?: boolean;
    onSelect(v: CategoryNode | undefined): void;
}) {
    const match = useMemo(() => {
        if(props.node.name.toLowerCase().indexOf(props.search.toLowerCase()) !== -1) return "root";

        const matches = function(n: CategoryNode) {
            if(n.name.toLowerCase().indexOf(props.search.toLowerCase()) !== -1) return true;
            return n.children.some(c => matches(c));
        }

        return matches(props.node);
    }, [props.node, props.search]);

    if(match === false && !props.showAll) {
        return null;
    }

    return (
        <>
            {props.node.children.length > 0 ? <ParentCategoryItem value={props.node} key={props.node.id.toString()} onClick={() => {
                if(props.node !== props.selected) {
                    props.onSelect(props.node)
                    return;
                }

                props.onSelect(undefined);
            }} /> : <CategoryItem value={props.node} onClick={() => props.onSelect(props.node)} selected={props.node === props.selected} />
            }
            {props.node.children.map(c => <SearchItem showAll={props.showAll || match === "root"} node={c} search={props.search} selected={props.selected} onSelect={props.onSelect} />)}
        </>
    )
}

function Item(props: {
    selected: CategoryNode | undefined;
    tree: CategoryNode;
    setSelected: (node: CategoryNode | undefined) => void;
    autoExpand?: boolean;
}) {
    const {selected, setSelected, tree} = props;

    let list = selected ? selected.children : tree.children;
    let parents: CategoryNode[] = []

    let cursor = selected;
    if(cursor && cursor.children.length === 0) {
        cursor = cursor.parent;
    }

    while(cursor) {
        parents.push(cursor);
        cursor = cursor.parent
    }

    parents = parents.reverse();

    return (
        <>
            {parents.map((c, index) =>
                <ParentCategoryItem key={c.id.toString()} value={c} onClick={() => {
                    if(c !== selected) {
                        setSelected(c)
                        return;
                    }

                    if(index > 0) {
                        setSelected(parents[index-1]);
                        return;
                    }

                    setSelected(undefined);
                }} />)}

            {selected && list.length === 0 && <CategoryItem key={selected.id.toString()} value={selected} selected />}
            {list.map(c =>
                <CategoryItem key={c.id.toString()} value={c} onClick={() => setSelected(c)} />
            )}
        </>
    )
}

function ParentCategoryItem(props: {
    value: CategoryNode
    onClick(): void;
}) {
    return (
        <ListItemButton key={props.value.id.toString()} className={selectedCategoryClass} onClick={props.onClick}>
            <ListItemText primary={props.value.name} style={{paddingRight: 16}} />
            <ExpandMoreIcon />
        </ListItemButton>
    )
}

function CategoryItem(props: {
    value: CategoryNode;
    selected?: boolean;
    onClick?(): void;
}) {
    const theme = useTheme();

    if(props.selected) {
        return (
            <div style={{
                border: "2px solid " + theme.palette.primary.light,
            }}>
                <ListItemButton>
                    <ListItemText primary={props.value.name} />
                </ListItemButton>
            </div>
        )
    }

    return (
        <ListItemButton key={props.value.id.toString()} onClick={props.onClick}>
            <ListItemText primary={props.value.name} />
        </ListItemButton>
    )

}

const selectedCategoryClass = css({
    "&.MuiListItemButton-root": {
        backgroundColor: grey["200"],
        border: "1px solid " + grey["300"],
    }
})

const listClass = css({
    "& .MuiListItemText-root": {
        whiteSpace: "nowrap",
    }
})